// The map app's state and its URL hash (web/main.ts keeps the state; this module only reads and writes the hash, so it
// can be tested without a page).
import type { Level } from "./api.ts";
import { parseScreen, screenParams } from "./screen.ts";
import type { State } from "./state.ts";
import { BAY_AREA_ID } from "../shared/regions.ts";
export type { State };

/** Areas in a comparison (Compare view), at most. */
export const MAX_COMPARE = 4;


const LEVELS: Level[] = ["us", "state", "metro", "county", "city", "zip"];

/** The map app's state from a URL hash ("#/table?level=zip&metric=rent&…"); unknown values are left out. */
export function readHash(hash: string, isMetric: (id: string) => boolean): Partial<State> {
  const raw = hash.replace(/^#\/?/, "");
  const [path, qs] = raw.includes("?") ? (raw.split("?", 2) as [string, string]) : raw.includes("=") ? ["", raw] : [raw, ""];
  const q = new URLSearchParams(qs);
  const s: Partial<State> = {};
  const sc = q.get("scope");
  if (sc === "ca" || sc === "us") s.scope = sc;
  const lv = q.get("level");
  if (lv && (LEVELS as string[]).includes(lv)) s.level = lv as Level;
  if (q.get("metric")) s.metric = q.get("metric")!;
  if (q.get("period")) s.period = q.get("period");
  if (q.get("geo")) s.geo = q.get("geo");
  if (q.get("freq") === "monthly") s.freq = "monthly";
  if (q.get("q")) s.q = q.get("q"); // checked against the questions once /api/metrics is in
  if (path === "table") s.view = "table";
  if (q.get("sort") === "asc") s.sort = "asc";
  if (path === "accuracy") s.view = "accuracy";
  if (path === "screen") s.view = "screen";
  if (path === "compare") s.view = "compare";
  if (path === "relate") s.view = "relate";
  if (q.get("y")) s.y = q.get("y");
  const w = q.get("within");
  // the relationship view's places; a table can also be held to the Bay Area's nine counties (from Ask)
  if (w && (/^(state:\d{2}|metro:\d{5}|county:\d{5})$/.test(w) || (w === BAY_AREA_ID && path === "table"))) s.within = w;
  if (q.get("ids")) s.compare = [...new Set(q.get("ids")!.split(",").filter((id) => /^(us|state:\d{2}|metro:\d{5}|county:\d{5}|zip:\d{5}|city:\d{7})$/.test(id)))].slice(0, MAX_COMPARE);
  const scr = parseScreen(q, isMetric);
  if (scr) s.screen = scr;
  return s;
}

/** The hash for a state: what writeHash mirrors into the URL. */
export function stateToHash(state: State): string {
  const q = new URLSearchParams({ scope: state.scope, level: state.level, metric: state.metric });
  if (state.period) q.set("period", state.period);
  if (state.geo) q.set("geo", state.geo);
  if (state.freq === "monthly") q.set("freq", "monthly");
  if (state.q) q.set("q", state.q);
  if (state.screen) for (const [k, v] of screenParams(state.screen)) q.append(k, v);
  if (state.compare.length) q.set("ids", state.compare.join(","));
  if (state.view === "relate" && state.y) q.set("y", state.y);
  if ((state.view === "relate" || (state.view === "table" && state.within === BAY_AREA_ID)) && state.within) q.set("within", state.within);
  const path = state.view === "map" ? "" : `/${state.view}`;
  return `#${path}?${q}`;
}
